import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  FileText, Download, PenTool, Check, Clock, AlertCircle,
  ChevronRight, Eye, Send, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature Canvas Component
const SignatureCanvas = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const save = () => {
    if (!hasDrawn) {
      toast.error('Te rugăm să desenezi semnătura');
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl, 'drawn');
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          className="w-full cursor-crosshair touch-none bg-white"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <p className="text-xs text-gray-500 text-center">Desenează semnătura ta folosind mouse-ul sau touchscreen-ul</p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={clear}>Șterge</Button>
        <Button variant="outline" onClick={onCancel}>Anulează</Button>
        <Button onClick={save} disabled={!hasDrawn}>
          <PenTool className="w-4 h-4 mr-2" />
          Salvează Semnătura
        </Button>
      </div>
    </div>
  );
};

// Text Signature Component
const TextSignature = ({ userName, onSave, onCancel }) => {
  const [name, setName] = useState(userName || '');
  const [font, setFont] = useState('cursive');

  const fonts = [
    { value: 'cursive', label: 'Cursiv' },
    { value: 'serif', label: 'Serif' },
    { value: 'monospace', label: 'Monospace' }
  ];

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Numele complet</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="Introdu numele tău"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Stil semnătură</label>
        <div className="flex gap-2">
          {fonts.map(f => (
            <button
              key={f.value}
              onClick={() => setFont(f.value)}
              className={`px-3 py-1 rounded border ${font === f.value ? 'bg-blue-100 border-blue-500' : 'border-gray-300'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-white">
        <p
          className="text-3xl text-center text-gray-800"
          style={{ fontFamily: font }}
        >
          {name || 'Semnătura ta'}
        </p>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel}>Anulează</Button>
        <Button onClick={() => onSave(name, 'typed')} disabled={!name.trim()}>
          <PenTool className="w-4 h-4 mr-2" />
          Confirmă Semnătura
        </Button>
      </div>
    </div>
  );
};

// Contract Card Component
export const ContractCard = ({ contract, onSign, onView }) => {
  const statusConfig = {
    draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
    pending_brand: { label: 'Așteaptă Brand', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    pending_influencer: { label: 'Așteaptă Creator', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    active: { label: 'Activ', color: 'bg-green-100 text-green-800', icon: Check },
    completed: { label: 'Finalizat', color: 'bg-blue-100 text-blue-800', icon: Check },
    cancelled: { label: 'Anulat', color: 'bg-red-100 text-red-800', icon: AlertCircle }
  };

  const status = statusConfig[contract.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  return (
    <div className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium">{contract.contract_data?.campaign_title || 'Contract'}</h4>
            <p className="text-sm text-gray-500">
              {new Date(contract.created_at).toLocaleDateString('ro-RO')}
            </p>
          </div>
        </div>
        <Badge className={status.color}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {status.label}
        </Badge>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-gray-500">Brand:</span>
          <span className="ml-1 font-medium">{contract.contract_data?.brand_name}</span>
        </div>
        <div>
          <span className="text-gray-500">Creator:</span>
          <span className="ml-1 font-medium">@{contract.contract_data?.influencer_username}</span>
        </div>
        <div>
          <span className="text-gray-500">Valoare:</span>
          <span className="ml-1 font-medium">{contract.contract_data?.total_amount} {contract.contract_data?.currency}</span>
        </div>
        <div>
          <span className="text-gray-500">Limbă:</span>
          <span className="ml-1 font-medium">{contract.language === 'en' ? 'Engleză' : 'Română'}</span>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <span>Brand:</span>
          {contract.brand_signature ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Clock className="w-4 h-4 text-yellow-500" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <span>Creator:</span>
          {contract.influencer_signature ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Clock className="w-4 h-4 text-yellow-500" />
          )}
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" onClick={() => onView(contract)}>
          <Eye className="w-4 h-4 mr-1" />
          Vezi
        </Button>
        {(['pending_brand', 'pending_influencer', 'draft'].includes(contract.status)) && (
          <Button size="sm" onClick={() => onSign(contract)}>
            <PenTool className="w-4 h-4 mr-1" />
            Semnează
          </Button>
        )}
      </div>
    </div>
  );
};

// Main Contract Manager Component
export const ContractManager = ({ collabId, applicationId, userRole, onContractCreated }) => {
  const { user, getAuthHeaders } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [signingContract, setSigningContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  const [signatureType, setSignatureType] = useState(null); // 'drawn' or 'typed'
  const [language, setLanguage] = useState('ro');

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const res = await fetch(`${API}/contracts?role=${userRole}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setContracts(data);
      }
    } catch (e) {
      console.error('Failed to fetch contracts:', e);
    } finally {
      setLoading(false);
    }
  };

  const createContract = async () => {
    if (!collabId || !applicationId) {
      toast.error('Selectează o colaborare și o aplicație');
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API}/contracts/create`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ collab_id: collabId, application_id: applicationId, language })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Contract generat cu succes');
        fetchContracts();
        if (onContractCreated) onContractCreated(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Eroare la generarea contractului');
      }
    } catch (e) {
      toast.error('Eroare la generarea contractului');
    } finally {
      setCreating(false);
    }
  };

  const signContract = async (signature, type) => {
    if (!signingContract) return;

    try {
      const res = await fetch(`${API}/contracts/${signingContract.contract_id}/sign`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ signature, signature_type: type })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.status === 'active' ? 'Contract semnat de ambele părți!' : 'Contract semnat cu succes');
        setSigningContract(null);
        setSignatureType(null);
        fetchContracts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Eroare la semnare');
      }
    } catch (e) {
      toast.error('Eroare la semnare');
    }
  };

  const downloadPDF = async (contractId) => {
    try {
      const res = await fetch(`${API}/contracts/${contractId}/pdf`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contract_${contractId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Nu s-a putut descărca PDF-ul');
      }
    } catch (e) {
      toast.error('Eroare la descărcare');
    }
  };

  if (loading) {
    return <div className="animate-pulse p-4 bg-gray-100 rounded-lg">Se încarcă contractele...</div>;
  }

  // Signature Modal
  if (signingContract && signatureType) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Semnează Contractul</h3>
          <p className="text-sm text-gray-600 mb-4">
            {signingContract.contract_data?.campaign_title}
          </p>
          
          {signatureType === 'drawn' ? (
            <SignatureCanvas
              onSave={signContract}
              onCancel={() => { setSignatureType(null); setSigningContract(null); }}
            />
          ) : (
            <TextSignature
              userName={user?.name}
              onSave={signContract}
              onCancel={() => { setSignatureType(null); setSigningContract(null); }}
            />
          )}
        </div>
      </div>
    );
  }

  // Signature Type Selection
  if (signingContract && !signatureType) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <h3 className="text-lg font-semibold mb-4">Alege tipul semnăturii</h3>
          <p className="text-sm text-gray-600 mb-6">
            Cum dorești să semnezi contractul?
          </p>
          
          <div className="space-y-3">
            <button
              onClick={() => setSignatureType('drawn')}
              className="w-full p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-colors flex items-center gap-3"
            >
              <PenTool className="w-6 h-6 text-blue-600" />
              <div className="text-left">
                <p className="font-medium">Semnătură desenată</p>
                <p className="text-sm text-gray-500">Desenează semnătura cu mouse-ul sau touchscreen</p>
              </div>
              <ChevronRight className="w-5 h-5 ml-auto text-gray-400" />
            </button>
            
            <button
              onClick={() => setSignatureType('typed')}
              className="w-full p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-colors flex items-center gap-3"
            >
              <FileText className="w-6 h-6 text-blue-600" />
              <div className="text-left">
                <p className="font-medium">Semnătură text</p>
                <p className="text-sm text-gray-500">Tastează numele tău într-un font stilizat</p>
              </div>
              <ChevronRight className="w-5 h-5 ml-auto text-gray-400" />
            </button>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => setSigningContract(null)}
          >
            Anulează
          </Button>
        </div>
      </div>
    );
  }

  // Contract View Modal
  if (viewingContract) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Detalii Contract</h3>
            <Button variant="ghost" onClick={() => setViewingContract(null)}>✕</Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">ID Contract</p>
                <p className="font-mono text-sm">{viewingContract.contract_id}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">Status</p>
                <p className="font-medium">{viewingContract.status}</p>
              </div>
            </div>

            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Detalii Campanie</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p><span className="text-gray-500">Titlu:</span> {viewingContract.contract_data?.campaign_title}</p>
                <p><span className="text-gray-500">Platformă:</span> {viewingContract.contract_data?.platform}</p>
                <p><span className="text-gray-500">Valoare:</span> {viewingContract.contract_data?.total_amount} {viewingContract.contract_data?.currency}</p>
                <p><span className="text-gray-500">Comision:</span> {viewingContract.contract_data?.commission_amount} {viewingContract.contract_data?.currency}</p>
                <p><span className="text-gray-500">Net Creator:</span> {viewingContract.contract_data?.net_amount} {viewingContract.contract_data?.currency}</p>
                <p><span className="text-gray-500">Deadline:</span> {viewingContract.contract_data?.deadline}</p>
              </div>
            </div>

            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Semnături</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Brand</p>
                  {viewingContract.brand_signature ? (
                    <div className="text-sm">
                      <Check className="w-4 h-4 text-green-500 inline" /> Semnat
                      <p className="text-xs text-gray-400">{viewingContract.brand_signed_at}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-yellow-600"><Clock className="w-4 h-4 inline" /> În așteptare</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Creator</p>
                  {viewingContract.influencer_signature ? (
                    <div className="text-sm">
                      <Check className="w-4 h-4 text-green-500 inline" /> Semnat
                      <p className="text-xs text-gray-400">{viewingContract.influencer_signed_at}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-yellow-600"><Clock className="w-4 h-4 inline" /> În așteptare</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => downloadPDF(viewingContract.contract_id)}>
                <Download className="w-4 h-4 mr-2" />
                Descarcă PDF
              </Button>
              <Button variant="outline" onClick={() => setViewingContract(null)}>
                Închide
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Contracte
        </h3>
        
        {userRole === 'brand' && collabId && applicationId && (
          <div className="flex items-center gap-2">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-sm border rounded px-2 py-1"
            >
              <option value="ro">Română</option>
              <option value="en">English</option>
            </select>
            <Button size="sm" onClick={createContract} disabled={creating}>
              {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Generează Contract
            </Button>
          </div>
        )}
      </div>

      {contracts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Nu ai contracte încă</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {contracts.map(contract => (
            <ContractCard
              key={contract.contract_id}
              contract={contract}
              onSign={setSigningContract}
              onView={setViewingContract}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractManager;
